// Opportunity → Quotes tab (CEO 2026-10-15, "Opportunity → Quote"): ONE list
// for what used to be the Estimate and Quote (sales order) tabs. A quote covers
// the client shopping (several items, totals shown or hidden) AND the buying
// decision (artwork approval, proofs); once approved it is a live order. An
// opportunity can hold several quotes — alternatives, revisions, or more than
// one buying decision.
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { orderStatusLabel, orderStatusTone } from "@/lib/orders";
import { timeAgo } from "@/lib/utils";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface OrderRow {
  id: string;
  orderNumber: number;
  projectId: string;
  clientId: string;
  clientName: string;
  statusStage: string;
  statusState: string;
  customerPo: string | null;
  inHandsDate: string;
  createdAt: string;
  updatedAt: string;
}

interface OrdersListResponse {
  rows: OrderRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

function defaultInHandsDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

export function ProjectQuotesTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const projectId = data.project.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queryKey = ["project", projectId, "orders", "quotes"] as const;
  const { data: list, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      api<OrdersListResponse>(
        `/orders?project_id=${projectId}&limit=100`,
      ),
  });
  // Cancelled quotes stay out of the list (as the old tabs did).
  const rows = (list?.rows ?? []).filter((o) => o.statusStage !== "cancelled" && o.statusState !== "cancelled");
  // Row-grounded presentation count (rail node) — drives the "convert the
  // presentation" affordance in the empty state when one already exists.
  const presentationCount =
    data.stages.find((s) => s.key === "presentation")?.count ?? 0;

  // `?new=1` opens the create form directly (OrderDetailPage's "New quote"
  // and the overview's next step link here with it).
  const [searchParams] = useSearchParams();
  const [creating, setCreating] = useState(
    () => searchParams.get("new") === "1",
  );
  const [inHandsDate, setInHandsDate] = useState(
    data.project.inHandsDate || defaultInHandsDate(),
  );
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api<{ order: { id: string; orderNumber: number } }>("/orders", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(body?.error ?? `Create failed (${err.status}).`);
      } else {
        setError("Create failed. Try again.");
      }
    },
    onSuccess: (result) => {
      setCreating(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey });
      // The project menu's "Quotes" count and header (Stephanie 2026-11-02 sweep).
      void queryClient.invalidateQueries({ queryKey: ["merchd.projectDetail"] });
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      navigate(`/projects/${projectId}/quotes/${result.order.id}`);
    },
  });

  const submitNew = (e: FormEvent): void => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(inHandsDate)) {
      setError("In-hands date is required (YYYY-MM-DD).");
      return;
    }
    setError(null);
    createMutation.mutate({
      project_id: projectId,
      in_hands_date: inHandsDate,
    });
  };

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
            Quotes{" "}
            {rows.length > 0 && <span className="font-normal text-muted">({rows.length})</span>}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Everything the client shops and approves — several items, totals shown or not, artwork for approval.
          </p>
        </div>
        {!creating && (
          <div className="flex items-center gap-2">
            {/* When a presentation exists, building the quote from it is the
                usual path — it leads; New quote is the outline alternative.
                The button only NAVIGATES to the presentation ("Create quote"
                lives there). */}
            {presentationCount > 0 && (
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/projects/${projectId}/presentation`)}
              >
                <ArrowRight className="h-3.5 w-3.5" /> Quote from presentation
              </Button>
            )}
            <Button
              type="button"
              variant={presentationCount > 0 ? "outline" : "primary"}
              onClick={() => {
                setError(null);
                setInHandsDate(data.project.inHandsDate || defaultInHandsDate());
                setCreating(true);
              }}
            >
              <Plus className="h-4 w-4" /> New quote
            </Button>
          </div>
        )}
      </div>

      {creating && (
        <form
          onSubmit={submitNew}
          className="mb-4 rounded-md border border-rule bg-white p-5"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr,auto,auto] md:items-end">
            <label className="block">
              <span className="mb-1 block text-xs text-muted">
                In-hands date
              </span>
              <Input
                type="date"
                value={inHandsDate}
                onChange={(e) => setInHandsDate(e.target.value)}
                autoFocus
                className="text-sm"
              />
            </label>
            <Button
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Creating…" : "Create quote"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setError(null);
              }}
              disabled={createMutation.isPending}
              className="min-h-[40px] text-sm text-muted hover:text-ink disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
          {error && (
            <p className="mt-2 text-sm text-risk" role="alert">{error}</p>
          )}
          <p className="mt-2 text-xs text-muted">
            Creates a draft quote. Add the items and artwork, then send it to
            the client to review and approve.
          </p>
        </form>
      )}

      {isError ? (
        <p role="alert" className="rounded-md border border-rule bg-white px-5 py-6 text-sm text-risk">
          We couldn&apos;t load this opportunity&apos;s quotes. Nothing has changed — refresh to try again.
        </p>
      ) : isLoading ? (
        <div className="h-24 animate-pulse rounded-md bg-white" aria-label="Loading quotes" />
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-rule bg-white px-5 py-12 text-center text-sm text-muted">
          No quotes on this opportunity yet.{" "}
          {presentationCount > 0 ? (
            <>
              Build one from the{" "}
              <Link to={`/projects/${projectId}/presentation`} className="font-semibold text-ink hover:text-muted">
                presentation →
              </Link>{" "}
              or start a blank one with <strong>New quote</strong>.
            </>
          ) : (
            <>Click <strong>New quote</strong> to draft one.</>
          )}
        </div>
      ) : (
        <div className={TABLE_WRAP} role="region" aria-label="Quotes" tabIndex={0}>
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Quote</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>In hands</th>
                <th scope="col" className={`${TH} hidden md:table-cell`}>Customer PO</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Updated</th>
              </tr>
            </thead>
            <tbody className="text-[15px]">
              {rows.map((o) => (
                <tr key={o.id} className={TROW}>
                  <td className={TD}>
                    <Link
                      to={`/projects/${projectId}/quotes/${o.id}`}
                      className="font-medium text-ink hover:underline"
                    >
                      Quote #{o.orderNumber}
                    </Link>
                  </td>
                  <td className={TD}>
                    {/* Combined order-status pill via the shared helper (UX S1)
                        — was a raw {statusState} enum ("changes_requested"). */}
                    <StatusPill tone={orderStatusTone(o.statusStage, o.statusState)}>
                      {orderStatusLabel(o.statusStage, o.statusState)}
                    </StatusPill>
                  </td>
                  <td className={`${TD} hidden sm:table-cell`}>{formatDate(o.inHandsDate)}</td>
                  <td className={`${TD} hidden text-muted md:table-cell`}>{o.customerPo ?? "—"}</td>
                  <td className={`${TD} hidden text-muted sm:table-cell`}>{timeAgo(o.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function formatDate(s: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
